import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import ShareButtons from "@/components/ShareButtons";
import { Users, BookOpen, User, ImageIcon, Plus, Filter, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const TYPE_META = {
  lore: { icon: BookOpen, color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", label: "Lore Entry" },
  character: { icon: User, color: "text-purple-500", bg: "bg-purple-500/10", border: "border-purple-500/20", label: "Character" },
  fan_art: { icon: ImageIcon, color: "text-pink-500", bg: "bg-pink-500/10", border: "border-pink-500/20", label: "Fan Art" },
};

const CommunityPage = () => {
  const { user } = useAuth();
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [universes, setUniverses] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contRes, uniRes] = await Promise.all([
          axios.get(`${API}/contributions`),
          axios.get(`${API}/universes`),
        ]);
        setContributions(contRes.data);
        setUniverses(uniRes.data);
      } catch { toast.error("Failed to load community content"); }
      setLoading(false);
    };
    fetchData();
  }, []);

  const getUniverseName = (uid) => universes.find(u => u.id === uid)?.name || "";

  const filtered = filter
    ? contributions.filter(c => c.type === filter)
    : contributions;

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-base)]">
      <SEOHead title="Community" description="Fan contributions — lore, characters, and art from the FanVerse community." />
      <Navbar />
      <main className="flex-1 pt-20 pb-16 px-4 max-w-5xl mx-auto w-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-500/20 bg-cyan-500/5 mb-3">
              <Users className="w-3.5 h-3.5 text-cyan-500" />
              <span className="text-xs font-mono tracking-wider text-cyan-500 uppercase">Community</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-2">Fan Contributions</h1>
            <p className="text-slate-400 text-sm">Lore, characters, and fan art from our community</p>
          </div>
          {user && (
            <Link to="/community/submit">
              <Button className="btn-primary gap-2" data-testid="submit-contribution-btn">
                <Plus className="w-4 h-4" /> Contribute
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          {["", "lore", "character", "fan_art"].map(t => (
            <button
              key={t || "all"}
              onClick={() => setFilter(t)}
              className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
                filter === t ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" : "bg-white/5 text-slate-400 border border-white/10 hover:text-white"
              }`}
              data-testid={`community-filter-${t || "all"}`}
            >
              {t ? TYPE_META[t]?.label : "All"}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="spinner"></div></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 card-surface">
            <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 mb-2">No contributions yet</p>
            {user ? (
              <Link to="/community/submit" className="text-cyan-400 text-sm hover:underline">Be the first to contribute!</Link>
            ) : (
              <Link to="/login" className="text-cyan-400 text-sm hover:underline">Sign in to contribute</Link>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="contributions-list">
            {filtered.map(c => {
              const meta = TYPE_META[c.type] || TYPE_META.lore;
              const Icon = meta.icon;
              return (
                <div key={c.id} className={`card-surface p-5 border-l-2 ${meta.border}`} data-testid={`contribution-${c.id}`}>
                  <div className="flex items-start gap-4">
                    {/* Images (fan art) */}
                    {c.images?.length > 0 && (
                      <img src={c.images[0]} alt={c.title} className="w-20 h-20 rounded-lg object-cover border border-white/10 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      {/* Meta line */}
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider ${meta.color}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                        {c.universe_id && (
                          <span className="text-[10px] text-slate-600">{getUniverseName(c.universe_id)}</span>
                        )}
                        {c.trusted_post && (
                          <span className="text-[9px] bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded font-mono">TRUSTED</span>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-1">{c.title}</h3>

                      {/* Content preview */}
                      <p className="text-slate-400 text-sm line-clamp-3 mb-3 leading-relaxed">{c.content}</p>

                      {/* Character details */}
                      {c.type === "character" && c.species && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          <span className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded">{c.species}</span>
                          {c.affiliation && <span className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded">{c.affiliation}</span>}
                          {(c.abilities || []).map((a, i) => (
                            <span key={i} className="text-[10px] bg-white/5 text-slate-400 px-2 py-0.5 rounded">{a}</span>
                          ))}
                        </div>
                      )}

                      {/* Footer: author + share */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {c.author_avatar ? (
                            <img src={c.author_avatar} alt="" className="w-5 h-5 rounded-full object-cover" />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center">
                              <User className="w-3 h-3 text-cyan-400" />
                            </div>
                          )}
                          <span className="text-xs text-cyan-400 font-medium" data-testid={`contribution-author-${c.id}`}>
                            {c.author_name}
                          </span>
                          <span className="text-[10px] text-slate-600">{new Date(c.created_at).toLocaleDateString()}</span>
                        </div>
                        <ShareButtons title={`${c.title} — FanVerse Community`} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default CommunityPage;
